import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import type { DataResponse, MaterialSlot } from "@/api/schemas";

export interface SlotyZMaterialovVysledok {
  vytvorene: MaterialSlot[];
  pouzite_existujuce: MaterialSlot[];
  prepnutych: number;
}

/**
 * „Vytvoriť sloty z materiálov" — zákazky, ktoré vznikli pred KOLOM 96
 * (KOLO 96).
 *
 * Import zapisoval dosku priamo na dielec, takže tab Sloty ostal prázdny
 * a dekor sa nedal vymeniť naraz. Zoskupenie robí **backend** — tú istú
 * službu volá aj import, takže „čo je jeden slot" má jednu definíciu.
 *
 * Undo je **jeden krok za celú operáciu** (bolo to jedno kliknutie nad
 * stovkami dielcov) a stojí na `rozpustit`: samotný `DELETE /sloty/{id}`
 * použitý slot nezmaže (409).
 */
export function useSlotyZMaterialov(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<SlotyZMaterialovVysledok, Error, void> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (): Promise<SlotyZMaterialovVysledok> => {
      const { data } = await api.post<DataResponse<SlotyZMaterialovVysledok>>(
        `/varianty/${variantaId}/sloty/z-materialov`,
      );
      return data.data;
    },
    onSuccess: (vysledok) => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
      if (vysledok.vytvorene.length === 0) return;
      const ids = vysledok.vytvorene.map((s) => s.id);
      undo?.pridaj({
        popis: "vytvorenie materiálov zákazky",
        vrat: async () => {
          await api.post(`/varianty/${variantaId}/sloty/rozpustit`, {
            slot_ids: ids,
          });
          await qc.invalidateQueries({
            queryKey: queryKeys.zakazky.detail(zakazkaId),
          });
        },
      });
    },
  });
}
